import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ensurePayrollRunForPeriod, listPayPeriods } from "./payrollApi";
import { usePayrollWorkflow } from "./payrollWorkflow";

function formatError(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || JSON.stringify(item)).join("; ");
  }
  return error?.message || "Request failed";
}

/**
 * Payroll ID already exists, so this route does not show a second create form.
 * It opens the pay run for that Payroll ID.
 */
export default function PayrollRunsCreate() {
  const navigate = useNavigate();
  const { setWorkflow, organisationId, organisationName, payPeriodId } = usePayrollWorkflow();
  const [phase, setPhase] = useState("opening");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!organisationId) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const { data } = await listPayPeriods();
        const rows = Array.isArray(data) ? data : [];
        if (cancelled) return;
        if (!rows.length) {
          setPhase("empty");
          return;
        }

        const selected =
          rows.find((row) => String(row.pay_period_id) === String(payPeriodId)) || rows[0];
        const name =
          selected.start_date && selected.end_date
            ? `${selected.start_date} → ${selected.end_date}`
            : "Payroll ID";
        const run = await ensurePayrollRunForPeriod({
          organisationId: selected.organisation_id || organisationId,
          payPeriodId: selected.pay_period_id,
        });
        if (cancelled) return;

        setWorkflow({
          organisationId: selected.organisation_id || organisationId,
          organisationName,
          payPeriodId: selected.pay_period_id,
          payPeriodLabel: name,
          payPeriodStart: selected.start_date,
          payPeriodEnd: selected.end_date,
          payrollRunId: run.payroll_run_id,
          payrollRunLabel: name,
        });

        navigate("/payroll-process?tab=process", {
          replace: true,
          state: {
            payrollIdNotice: {
              kind: "ready",
              name,
            },
          },
        });
      } catch (e) {
        if (!cancelled) {
          setPhase("error");
          setError(formatError(e));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, organisationId, organisationName, payPeriodId, setWorkflow]);

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-10">
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm p-6">
        {phase === "empty" ? (
          <>
            <h2 className="text-lg font-semibold text-slate-900">Create a Payroll ID first</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              A pay run opens only after a Payroll ID exists. Create the Payroll ID, and you will
              go straight to the pay run page.
            </p>
            <button
              type="button"
              onClick={() => navigate("/payroll-process?tab=periods")}
              className="mt-5 inline-flex items-center rounded-lg bg-[#0f2d52] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#163e6b]"
            >
              Create Payroll ID
            </button>
          </>
        ) : phase === "error" ? (
          <>
            <h2 className="text-lg font-semibold text-slate-900">Could not open the pay run</h2>
            <p className="mt-2 text-sm text-amber-800">{error}</p>
            <button
              type="button"
              onClick={() => navigate("/payroll-process?tab=periods")}
              className="mt-5 inline-flex items-center rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Back to Payroll ID
            </button>
          </>
        ) : (
          <>
            <h2 className="text-lg font-semibold text-slate-900">Opening pay run</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              This Payroll ID is already created. Taking you to the pay run page.
            </p>
          </>
        )}
      </section>
    </div>
  );
}
